import type { ManualEditEvent } from "@tscircuit/props"
import type { AnyCircuitElement } from "circuit-json"
import { getFullConnectivityMapFromCircuitJson } from "circuit-json-to-connectivity-map"
import type { GraphicsObject } from "graphics-debug"
import {
  convertElementToPrimitives,
  createPrimitiveMetadataIndex,
} from "lib/convert-element-to-primitive"
import type { GridConfig, Primitive } from "lib/types"
import { addInteractionMetadataToPrimitives } from "lib/util/addInteractionMetadataToPrimitives"
import {
  buildErrorPreviewElementIndexes,
  createTransformForBounds,
  getErrorPreviewBounds,
  getRelatedIdsForError,
} from "lib/util/error-preview"
import { findErrorElementById, getErrorId } from "lib/util/get-error-id"
import {
  animateTransform,
  cancelTransformAnimation,
} from "lib/util/transform-animation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { applyToPoint, inverse, type Matrix } from "transformation-matrix"
import {
  getElementNetId,
  getXRayDisplayName,
  getXRayGroups,
  isXRayCopper,
} from "lib/x-ray-net"
import { useGlobalStore } from "../global-store"
import { WebGpuElementsRenderer } from "./WebGpuElementsRenderer"
import { CanvasPrimitiveRenderer } from "./CanvasPrimitiveRenderer"
import { DebugGraphicsOverlay } from "./DebugGraphicsOverlay"
import { type BoundsSelection, DimensionOverlay } from "./DimensionOverlay"
import { EditPlacementOverlay } from "./EditPlacementOverlay"
import { ErrorOverlay } from "./ErrorOverlay"
import {
  getPrimitivesUnderPoint,
  MouseElementTracker,
} from "./MouseElementTracker"
import { PcbGroupOverlay } from "./PcbGroupOverlay"
import { RatsNestOverlay } from "./RatsNestOverlay"
import { ToolbarOverlay } from "./ToolbarOverlay"
import { WarningGraphicsOverlay } from "./WarningGraphicsOverlay"

export interface CanvasElementsRendererProps {
  elements: AnyCircuitElement[]
  debugGraphics?: GraphicsObject | null
  transform?: Matrix
  setTransform?: (transform: Matrix) => void
  width?: number
  height?: number
  renderer?: "webgpu" | "canvas"
  grid?: GridConfig
  allowEditing: boolean
  focusOnHover?: boolean
  onBoundsSelected?: (bounds: BoundsSelection) => void
  onContextMenuOpenChange?: (open: boolean) => void
  cancelPanDrag: () => void
  onCreateEditEvent: (event: ManualEditEvent) => void
  onModifyEditEvent: (event: Partial<ManualEditEvent>) => void
}

export const CanvasElementsRenderer = (props: CanvasElementsRendererProps) => {
  const { transform, elements } = props
  const {
    hoveredErrorId,
    focusedErrorId,
    isShowingCopperPours,
    selectedLayer,
  } = useGlobalStore((state) => ({
    hoveredErrorId: state.hovered_error_id,
    focusedErrorId: state.focused_error_id,
    isShowingCopperPours: state.is_showing_copper_pours,
    selectedLayer: state.selected_layer,
  }))
  const activeErrorId = focusedErrorId ?? hoveredErrorId

  const elementsToRender = useMemo(
    () =>
      isShowingCopperPours
        ? elements
        : elements.filter((elm) => elm.type !== "pcb_copper_pour"),
    [elements, isShowingCopperPours],
  )

  const primitivesWithoutInteractionMetadata = useMemo(() => {
    const metadataIndex = createPrimitiveMetadataIndex(elements)
    return elementsToRender.flatMap((element) =>
      convertElementToPrimitives(element, elements, metadataIndex),
    )
  }, [elementsToRender, elements])
  const connectivityMap = useMemo(
    () => getFullConnectivityMapFromCircuitJson(elements as any),
    [elements],
  )

  const [xRayNetIds, setXRayNetIds] = useState<string[]>([])
  // Net IDs are generated by the connectivity map and may change with the scene.
  useEffect(() => setXRayNetIds([]), [connectivityMap])
  const xRayElements = useMemo(
    () =>
      xRayNetIds.length > 0
        ? elementsToRender.filter(
            (element) =>
              isXRayCopper(element) &&
              xRayNetIds.includes(
                getElementNetId(element, connectivityMap) ?? "",
              ),
          )
        : undefined,
    [elementsToRender, connectivityMap, xRayNetIds],
  )
  const hoverPrimitives = useMemo(() => {
    if (!xRayElements) return primitivesWithoutInteractionMetadata
    const selectedElements = new Set(xRayElements)
    return primitivesWithoutInteractionMetadata.filter(
      (primitive) =>
        (primitive._element?.type === "pcb_smtpad" ||
          primitive._element?.type === "pcb_plated_hole") &&
        selectedElements.has(primitive._element),
    )
  }, [primitivesWithoutInteractionMetadata, xRayElements])

  const getNetAtPoint = (point: { x: number; y: number }) => {
    if (!transform) return undefined
    const describeNet = (netId: string, element: AnyCircuitElement) => ({
      netId,
      displayName: getXRayDisplayName(element, elements, connectivityMap),
      groups: getXRayGroups(netId, elements, connectivityMap),
    })
    const hits = getPrimitivesUnderPoint(
      primitivesWithoutInteractionMetadata,
      applyToPoint(inverse(transform), point),
      transform,
      selectedLayer,
      xRayNetIds.length > 0,
    )
    const copper = hits.filter(
      (p) => isXRayCopper(p._element) && p.layer !== "drill",
    )
    copper.sort(
      (a, b) =>
        Number(b.layer === selectedLayer) - Number(a.layer === selectedLayer),
    )
    // Inspected nets render above dimmed copper; prefer the frontmost selected hit.
    for (const primitive of copper) {
      const net = getElementNetId(primitive._element, connectivityMap)
      if (net && xRayNetIds.includes(net))
        return describeNet(net, primitive._element!)
    }
    for (const primitive of copper) {
      const net = getElementNetId(primitive._element, connectivityMap)
      if (net) return describeNet(net, primitive._element!)
    }
  }

  const [hoverState, setHoverState] = useState({
    drawingObjectIdsWithMouseOver: new Set<string>(),
    primitiveIdsInMousedOverNet: [] as string[],
  })
  const [hoveredComponentIds, setHoveredComponentIds] = useState<string[]>([])
  const currentTransformRef = useRef<Matrix | null>(transform ?? null)
  const zoomAnimationFrameRef = useRef<number | null>(null)

  const elementIndexes = useMemo(
    () => buildErrorPreviewElementIndexes(elements),
    [elements],
  )

  const errorRelatedIds = useMemo(() => {
    if (!activeErrorId) return []

    const errorElements = elements.filter((el): el is any =>
      el.type.includes("error"),
    )

    const activeError = errorElements.find((el, index) => {
      return getErrorId(el, index) === activeErrorId
    })

    if (!activeError) return []

    return getRelatedIdsForError(activeError)
  }, [activeErrorId, elements])

  useEffect(() => {
    if (transform) {
      currentTransformRef.current = transform
    }
  }, [transform])

  useEffect(() => {
    return () => {
      cancelTransformAnimation(zoomAnimationFrameRef.current)
    }
  }, [])

  useEffect(() => {
    if (!props.width || !props.height || !props.setTransform || !focusedErrorId)
      return

    const focusedError = findErrorElementById(elements, focusedErrorId)

    if (!focusedError) return

    const previewBounds = getErrorPreviewBounds({
      error: focusedError,
      indexes: elementIndexes,
    })

    if (!previewBounds) return

    const startTransform = currentTransformRef.current ?? transform
    if (!startTransform) return

    const targetTransform = createTransformForBounds({
      bounds: previewBounds,
      width: props.width,
      height: props.height,
    })

    cancelTransformAnimation(zoomAnimationFrameRef.current)

    animateTransform({
      startTransform,
      endTransform: targetTransform,
      durationMs: 420,
      setAnimationFrameId: (animationFrameId) => {
        zoomAnimationFrameRef.current = animationFrameId
      },
      onUpdate: (nextTransform) => {
        currentTransformRef.current = nextTransform
        props.setTransform?.(nextTransform)
      },
    })
  }, [
    focusedErrorId,
    elements,
    elementIndexes,
    props.height,
    props.setTransform,
    props.width,
  ])

  const primitives = useMemo(() => {
    if (xRayNetIds.length > 0) return primitivesWithoutInteractionMetadata
    const combinedPrimitiveIds = [
      ...hoverState.primitiveIdsInMousedOverNet,
      ...errorRelatedIds,
    ]

    return addInteractionMetadataToPrimitives({
      primitivesWithoutInteractionMetadata,
      drawingObjectIdsWithMouseOver: hoverState.drawingObjectIdsWithMouseOver,
      primitiveIdsInMousedOverNet: combinedPrimitiveIds,
    })
  }, [
    primitivesWithoutInteractionMetadata,
    hoverState,
    errorRelatedIds,
    xRayNetIds,
  ])

  const onMouseOverPrimitives = useCallback(
    (primitivesHoveredOver: Primitive[]) => {
      const primitiveIdsInMousedOverNet: string[] = []
      for (const primitive of primitivesHoveredOver) {
        if (primitive._element) {
          const connectedPrimitivesList = connectivityMap.getNetConnectedToId(
            "pcb_port_id" in primitive._element
              ? primitive._element?.pcb_port_id!
              : "pcb_trace_id" in primitive._element
                ? primitive._element?.pcb_trace_id!
                : "",
          )
          primitiveIdsInMousedOverNet.push(
            ...connectivityMap.getIdsConnectedToNet(connectedPrimitivesList!),
          )
        }
      }

      const drawingObjectIdsWithMouseOver = new Set(
        primitivesHoveredOver.map((p) => p._pcb_drawing_object_id),
      )

      setHoverState({
        drawingObjectIdsWithMouseOver,
        primitiveIdsInMousedOverNet,
      })

      const componentIds = primitivesHoveredOver
        .map((primitive) => {
          if (
            primitive._parent_pcb_component?.type === "pcb_component" &&
            primitive._parent_pcb_component.pcb_component_id
          ) {
            return primitive._parent_pcb_component.pcb_component_id
          }
          if (
            primitive._element?.type === "pcb_component" &&
            primitive._element.pcb_component_id
          ) {
            return primitive._element.pcb_component_id
          }
          return null
        })
        .filter((id): id is string => Boolean(id))

      setHoveredComponentIds(Array.from(new Set(componentIds)))
    },
    [connectivityMap],
  )

  return (
    <MouseElementTracker
      elements={elementsToRender}
      transform={transform}
      primitives={hoverPrimitives}
      selectedLayer={selectedLayer}
      onMouseHoverOverPrimitives={onMouseOverPrimitives}
    >
      <EditPlacementOverlay
        disabled={!props.allowEditing}
        transform={transform}
        soup={elements}
        cancelPanDrag={props.cancelPanDrag}
        onCreateEditEvent={props.onCreateEditEvent}
        onModifyEditEvent={props.onModifyEditEvent}
      >
        <DimensionOverlay
          transform={transform!}
          focusOnHover={props.focusOnHover}
          primitives={primitivesWithoutInteractionMetadata}
          onBoundsSelected={props.onBoundsSelected}
          cancelPanDrag={props.cancelPanDrag}
        >
          <ToolbarOverlay
            elements={elements}
            getNetAtPoint={getNetAtPoint}
            xRayNetIds={xRayNetIds}
            onXRayNetsChange={setXRayNetIds}
            onContextMenuOpenChange={props.onContextMenuOpenChange}
          >
            <ErrorOverlay transform={transform} elements={elements}>
              <RatsNestOverlay transform={transform} soup={elements}>
                <PcbGroupOverlay
                  transform={transform}
                  elements={elements}
                  hoveredComponentIds={hoveredComponentIds}
                >
                  <DebugGraphicsOverlay
                    transform={transform}
                    debugGraphics={props.debugGraphics}
                  >
                    <WarningGraphicsOverlay
                      transform={transform}
                      elements={elements}
                    >
                      {props.renderer === "canvas" ? (
                        <CanvasPrimitiveRenderer
                          transform={transform}
                          primitives={primitives}
                          elements={elementsToRender}
                          xRayElements={xRayElements}
                          width={props.width}
                          height={props.height}
                          grid={props.grid}
                        />
                      ) : (
                        <WebGpuElementsRenderer
                          transform={transform}
                          primitives={primitives}
                          elements={elements}
                          xRayElements={xRayElements}
                          width={props.width}
                          height={props.height}
                          grid={props.grid}
                        />
                      )}
                    </WarningGraphicsOverlay>
                  </DebugGraphicsOverlay>
                </PcbGroupOverlay>
              </RatsNestOverlay>
            </ErrorOverlay>
          </ToolbarOverlay>
        </DimensionOverlay>
      </EditPlacementOverlay>
    </MouseElementTracker>
  )
}
